import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import qs from 'query-string';

import { userSelectors } from 'reducers';
import { parseFullName } from 'utils/helpers';
import {
    subscriptionStatus,
    RoleNames,
    roleCategories,
    queryStringifyOptions,
} from 'utils/constants';
import { theme } from 'config';
import { texts } from 'utils/languages/fr';
import {
    useGetAggregatedDistibutorDataQuery,
    useGetRolesQuery,
    useGetSubscriptionsQuery,
} from 'api/main';

import { CustomSelect, Loader } from 'components';
import { DashboardSection, InfoCard, DashboardTemplate } from '../common-components';
import OngoingSubscriptionAnalytics from './OngoingSubscriptionAnalytics';
import FinishedSubscriptionAnalytics from './FinishedSubscriptionAnalytics';
import { Box, Grid, MenuItem, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    filterInput: {
        width: 250,
        marginBottom: theme.spacing(4),
        '&:not(last-child)': {
            marginRight: theme.spacing(5),
        },
    },
    container: {
        width: '80%',
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    },
    firstGridRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
    },
    gridRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: '0 15% 0 8%',
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}));

export const pieChartOptions = {
    legend: { show: false },
    stroke: { show: true, colors: [theme.palette.grey[100]] },
    chart: {
        fontFamily: 'Inter, sans-serif',
        dropShadow: { enabled: false },
        events: {
            mounted: chart => {
                chart.windowResizeHandler();
            },
        },
    },
    theme: {
        mode: 'light',
        palette: 'palette1',
        monochrome: {
            enabled: true,
            color: theme.palette.primary.main,
            shadeTo: 'light',
            shadeIntensity: 0.75,
        },
    },
    tooltip: {
        style: {
            fontSize: '14px',
        },
        fill: {
            colors: ['#000'],
        },
    },
    dataLabels: {
        enabled: true,
        dropShadow: { enabled: false },
        offsetX: 5,
        offsetY: 5,
        background: {
            enabled: false,
            dropShadow: {
                enabled: false,
            },
        },
        style: {
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            colors: [theme.palette.grey[800]],
        },
    },
};

export const subscriptionNumberFormatter = val => `${val} souscription${val === 1 ? '' : 's'}`;

export const parseCustomData = (data, item, key) => {
    if (!data) return [];
    if (item === 'all') {
        return Object.values(data)
            .filter(x => typeof x === 'object')
            .map(val => {
                return val ? Object.values(val).reduce((acc, crt) => acc + +crt[key], 0) : null;
            });
    } else {
        if (!data[item]) return [];
        return Object.values(data[item]).map(it => +it[key]);
    }
};

export const customTotalParser = (data, item, key) => {
    if (!data) return 0;
    if (item === 'all')
        return Object.values(data)
            .filter(x => typeof x === 'object')
            .reduce((total, val) => {
                const value = val ? Object.values(val).reduce((acc, crt) => acc + +crt[key], 0) : 0;
                return total + value;
            }, 0);
    if (!data[item]) return 0;
    return Object.values(data[item]).reduce((acc, crt) => acc + +crt[key], 0);
};

const AdminDistributorDashboard = () => {
    const navigate = useNavigate();
    const classes = useStyles();

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const adminDistributorRoles = useMemo(
        () =>
            roles.filter(
                r =>
                    r.category?.name === roleCategories.ADMIN_DISTRIBUTORS ||
                    r.category?.name === roleCategories.DISTRIBUTORS
            ),
        [roles]
    );

    const filters = useMemo(
        () => ({
            pagination: false,
            companyId: [currentSubscription?.distributorCompany?.id],
            emailConfirmed: 1,
        }),
        [currentSubscription]
    );

    const [distributor, setDistributor] = useState('all');

    const isAdmin = useMemo(
        () =>
            currentSubscription?.role.name === RoleNames.ADMIN_CIF ||
            currentSubscription?.role.name === RoleNames.PSI,
        [currentSubscription]
    );

    const subscriptionFinished = useMemo(
        () => currentSubscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [currentSubscription]
    );

    const canPreviewDistributorClientSpace = useMemo(
        () => currentSubscription?.distributorCompany?.documentsValidated,
        [currentSubscription]
    );

    const { data: distributorData = {}, isLoading: dataLoader } =
        useGetAggregatedDistibutorDataQuery(
            {
                cifId: distributor === 'all' ? undefined : distributor,
            },
            {
                skip:
                    currentSubscription.indirectUntransferred ||
                    (!subscriptionFinished && !canPreviewDistributorClientSpace),
            }
        );

    const {
        data: { subscriptions: distributorList = [] } = { subscriptions: [] },
        isFetching: usersLoader,
    } = useGetSubscriptionsQuery(
        { ...filters, role: adminDistributorRoles.map(role => role.id) },
        { skip: !adminDistributorRoles.length || !isAdmin }
    );

    const handleChangeDistributor = useCallback(e => setDistributor(e.target.value), []);

    const handleInfoCardFilter = useCallback(
        payload => () => {
            navigate(`/investors?${qs.stringify(payload, queryStringifyOptions)}`);
        },
        [navigate]
    );

    return (
        <DashboardTemplate useDahboardPageTitle>
            <DashboardSection title={texts.ACTIONS_TO_TAKE}>
                <Loader loading={dataLoader || rolesLoader || usersLoader} marginTop={10}>
                    {isAdmin && (
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            value={distributor}
                            onChange={handleChangeDistributor}
                        >
                            <MenuItem value="all">{texts.USERS.toUpperCase()}</MenuItem>
                            {distributorList.map(d => (
                                <MenuItem key={d.id} value={d.user.id}>
                                    {parseFullName(d.user)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    )}
                    <Box width="100%" marginBottom={3}>
                        <Typography variant="body1" gutterBottom>
                            <b>{texts.SUBSCRIPTIONS_STATE}</b>
                        </Typography>
                        <Grid
                            container
                            spacing={0}
                            direction="column"
                            className={classes.container}
                        >
                            <Grid item className={classes.firstGridRow}>
                                <Typography>{texts.DURATION}</Typography>
                                <Typography>{texts.SUBSCRIPTION_NUMBER}</Typography>
                            </Grid>
                            <Grid
                                item
                                className={classes.gridRow}
                                onClick={handleInfoCardFilter({ transferred03: 1 })}
                            >
                                <Typography>0 - 3 {texts.DAYS}</Typography>
                                <Typography>{distributorData.less_than_3_days ?? 0}</Typography>
                            </Grid>
                            <Grid
                                item
                                className={classes.gridRow}
                                onClick={handleInfoCardFilter({ transferred37: 1 })}
                            >
                                <Typography>4 - 7 {texts.DAYS}</Typography>
                                <Typography>{distributorData.between_4_and_7_days ?? 0}</Typography>
                            </Grid>
                            <Grid
                                item
                                className={classes.gridRow}
                                onClick={handleInfoCardFilter({ transferred730: 1 })}
                            >
                                <Typography>8 - 30 {texts.DAYS}</Typography>
                                <Typography>
                                    {distributorData.between_8_and_30_days ?? 0}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                className={classes.gridRow}
                                onClick={handleInfoCardFilter({ transferred30plus: 1 })}
                            >
                                <Typography>30 {texts.DAYS} +</Typography>
                                <Typography>{distributorData.more_than_30_days ?? 0}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    <Grid container spacing={2}>
                        <InfoCard
                            onClick={handleInfoCardFilter({ pendingActivation: 1 })}
                            number={
                                distributorData.indirect_subscriptions_transferred_pending_activation
                            }
                            label={texts.SUBSCRIPTIONS_PENDING_ACTIVATION}
                        />
                        <InfoCard
                            onClick={handleInfoCardFilter({ pendingTransfer: 1 })}
                            number={distributorData.indirect_subscriptions_pending_transfer}
                            label={texts.SUBSCRIPTIONS_PENDING_TRANSFER}
                        />
                        <InfoCard
                            onClick={handleInfoCardFilter({ alertTypes: ['critical'] })}
                            number={distributorData.nr_critical_warnings}
                            label={texts.CRITICAL_WARNING_ONGOING_SUBSCRIPTIONS}
                        />
                    </Grid>
                </Loader>
            </DashboardSection>
            <OngoingSubscriptionAnalytics
                isAdmin={isAdmin}
                classes={classes}
                distributors={distributorList}
                distributorsLoader={usersLoader}
            />
            <FinishedSubscriptionAnalytics
                isAdmin={isAdmin}
                classes={classes}
                distributors={distributorList}
                distributorsLoader={usersLoader}
            />
        </DashboardTemplate>
    );
};

export default AdminDistributorDashboard;
